import React from "react";
import { useLocation } from "react-router-dom";

/**
 * Lightweight SEO helper: sets <title>, meta tags, and JSON-LD structured data.
 * Avoids react-helmet to keep deps minimal.
 */
export default function Seo({ title, description, keywords = [], ogImage, structuredData, canonicalPath }) {
  const loc = useLocation();
  React.useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attrName, value) => {
      if (value == null) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, attr, val] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
        if (attr && val) el.setAttribute(attr, val);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (keywords?.length) {
      setMeta('meta[name="keywords"]', "content", keywords.join(", "));
    }
    if (ogImage) {
      setMeta('meta[property="og:image"]', "content", ogImage);
      setMeta('meta[name="twitter:image"]', "content", ogImage);
    }
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    // Canonical
    const canonicalUrl = (process.env.REACT_APP_BACKEND_URL || "") + (canonicalPath || loc.pathname);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    // Structured data
    const existing = document.head.querySelectorAll('script[data-seo-jsonld="true"]');
    existing.forEach((n) => n.remove());
    const items = Array.isArray(structuredData) ? structuredData : (structuredData ? [structuredData] : []);
    items.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    };
  }, [title, description, keywords?.join("|"), ogImage, JSON.stringify(structuredData), canonicalPath, loc.pathname]);

  return null;
}
